import { DASHBOARD_TYPE } from '@const/dashboard.constants';
import { BOX_TYPE } from '@const/pageResponse.constants';
import { BasicResponseDTO } from './basicResponse.dto';
import { ItemGroup, ItemGroupExcludeWithLabels } from './customGroup.dto';
import { MenuItem } from './menuResponse.dto';
import { CapacityTableBox, ChartType, GraphBase, GraphInterval, MetricAggregate, TableBox } from './pageResponse.dto';

//#region Shared types

export type DashboardType = typeof DASHBOARD_TYPE[keyof typeof DASHBOARD_TYPE];

export type DashboardTableType = 'capacity_table' | 'alerting_table';

export type DashboardItemType = 'graph' | 'custom_graph' | 'custom_graph_per_item' | DashboardTableType | typeof BOX_TYPE.FourGraphBox;

/** Per-item graph settings stored alongside the dashboard item. */
export interface DashboardGraphOptions {
  per_item_width?: number;
  per_item_height?: number;
  legend?: boolean;
  url?: string;
}

export class CreateDTO {
  label: string;
  parent: string;
  group_id?: number;
}

export class CreateResponseDTO extends BasicResponseDTO {
  data: {
    id: string;
    label?: string;
    parent?: string;
  };
}

//#endregion

//#region Dashboard

export class DefaultDashboard {
  dashboard: DashboardDTO;
  path: MenuItem[];
}

export class DashboardTreeItem {
  type: string; // dashboard, folder, group
  id: string;
  label: string;
  children?: DashboardTreeItem[];
}

export class DashboardDTO {
  dashboard_id: string;
  label: string;
  type: DashboardType;
  groups: DashboardGroupDTO[];
  folder: string;
  default: boolean;
  width: number;
}

export class UpdateDashboardDTO {
  label?: string;
  parent?: string;
  group_id?: number;
  width?: number;
}

export class DefaultDashboardDTO extends BasicResponseDTO {
  data: DefaultDashboard;
}

export class DashboardTreeItemResponseDTO extends BasicResponseDTO {
  data: DashboardTreeItem[];
}

//#endregion

//#region Folder

export class DashboardFolder {
  dashboard_folder_id: string;
  label: string;
  type: DashboardType;
}

export class GroupDashboardFolder extends MenuItem {
  group_id: number;
}

export class UpdateDashboardFolderDTO {
  label?: string;
  parent?: string;
  group_id?: number;
}

export class GroupDashboardFoldersDTO extends BasicResponseDTO {
  data: GroupDashboardFolder[];
}

//#endregion

//#region Group

export class DashboardGroup {
  graphs: DashboardGraph[];
  tables: DashboardTable[];
  label: string;
  width: number;
  height: number;
  x: number;
  y: number;
  dashboard_group_id: string;
}

export class DashboardGroupDTO {
  dashboard_group_id: string;
  dashboard_id?: string;
  label: string;
}

export class UpdateDashboardGroupDTO {
  width: number;
  height: number;
  x: number;
  y: number;
  label: string;
  dashboard_id: string;

  constructor(group?: DashboardGroup) {
    if (!group) return;

    this.width = group.width;
    this.height = group.height;
    this.x = group.x;
    this.y = group.y;
    this.label = group.label;
  }
}

export class DashboardGroupsDTO extends BasicResponseDTO {
  data: DashboardGroupDTO[];
}

//#endregion

//#region Item

export interface DashboardItem {
  dashboard_item_id: string;
  label?: string;
  graphLabel?: string;
  width: number;
  height: number;
  x: number;
  y: number;
  type: DashboardItemType;
  dashboard_group_id?: string;

  // graph related
  tab?: string;
  subtab?: string;
  tabId?: string;
  url?: string;
  interval?: GraphInterval;
}

export class DashboardItem {
  /** User-built graphs have no page identity; template siblings even share the item url. */
  static isCustom(di: DashboardItem) {
    return di.type === 'custom_graph' || di.type === 'custom_graph_per_item';
  }

  /** Whether both stand for the same page item, i.e. a dashboard would show it twice. */
  static equals(a: DashboardItem, b: DashboardItem) {
    if (DashboardItem.isCustom(a) || DashboardItem.isCustom(b)) return a.dashboard_item_id === b.dashboard_item_id;
    // tabId only exists on items saved since tabs got ids; older ones stand for the same tab by label alone
    const sameTab = a.tab === b.tab && a.subtab === b.subtab && (!a.tabId || !b.tabId || a.tabId === b.tabId);
    return a.type === b.type && a.url === b.url && sameTab && a.interval === b.interval;
  }
}

export class DashboardGraph extends GraphBase implements DashboardItem {
  // so it is placed to end before compact
  static readonly DEFAULT_COORDINATE = 99;
  dashboard_item_id: string;
  label: string;
  width: number;
  height: number;
  x: number;
  y: number;
  type: DashboardItemType;

  // graph related
  url?: string;
  interval?: GraphInterval;
  tab?: string;
  subtab?: string;
  tabId?: string;
  id?: string;

  // graph per item related
  labels?: string[];
  options?: DashboardGraphOptions;

  static isMultiGraph(di: DashboardItem): di is DashboardGraph {
    return di.type === 'custom_graph_per_item';
  }
}

export class DashboardTableDataRequest {
  tab?: string;
  subtab?: string;
  tabId?: string;
  url?: string;
  id?: string;
  type?: DashboardTableType;
  dashboard_item_id?: string;
}

export class DashboardTable extends DashboardTableDataRequest implements DashboardItem {
  type: DashboardTableType;
  width: number;
  height: number;
  x: number;
  y: number;
  dashboard_item_id: string;
  label?: string;
}

export class FindItem {
  interval?: GraphInterval;
  dashboard_item_id: string;
  dashboard_group: {
    label: string;
    dashboard_group_id: string;
  };
  dashboard: {
    label: string;
    dashboard_id: string;
  };
  group_id?: number;
  type?: string;
  path?: string;
  alerting_group_id?: string; // alerting related
  alerting_shared_group_id?: number; // user group the alert group is shared with
  legend?: boolean;
}

export class CreateDashboardItemDTO {
  dashboard_group_id: string;
  url?: string;
  tab?: string;
  subtab?: string;
  tabId?: string;
  id?: string;
  interval?: GraphInterval;
  type: DashboardItemType;
  options?: DashboardGraphOptions;
}

export class CreateDashboardAlertDTO {
  dashboard_group_id: string;
  alerting_group_id: string;
}

export class ApplyTemplateDTO {
  dashboard_id: string;
  item_id: string;
  legend?: boolean;
}

export class UpdateDashboardItemDTO {
  width?: number;
  height?: number;
  url?: string;
  x?: number;
  y?: number;
  interval?: GraphInterval;
  tab?: string;
  subtab?: string;
  tabId?: string;
  dashboard_group_id: string;
  /** Omitted keeps the stored options, null removes them */
  options?: DashboardGraphOptions | null;

  constructor(item?: DashboardItem) {
    if (!item) return;

    this.width = item.width;
    this.height = item.height;
    this.url = item.url;
    this.x = item.x;
    this.y = item.y;
    this.interval = item.interval;
    if (DashboardGraph.isMultiGraph(item)) {
      this.options = item.options;
    }
  }
}

export class FindDashboardItemDTO {
  url?: string;
  tab?: string;
  subtab?: string;
  interval?: GraphInterval;
  tabId?: string;
  id?: string;
  type?: DashboardItemType;
}

export class DashboardTableData extends BasicResponseDTO {
  data: CapacityTableBox | TableBox;
}

export class FindItemsDTO extends BasicResponseDTO {
  data: FindItem[];
}

export class DashboardItemResponseDTO extends BasicResponseDTO {
  data: DashboardItem;
}

//#endregion

//#region Custom graph

export class CustomGraphMetric {
  metric: string;
  type: ChartType;
  stacked: boolean;
  order: number;
  aggregation?: MetricAggregate;
  color?: string;
}

export class DashboardCustomGraph extends ItemGroupExcludeWithLabels {
  dashboard_custom_graph_id: string;
  dashboard_group_id: string;
  item_group_id?: string;
  label: string;

  // item group related
  class?: string;
  hw_type?: string;
  subsystem: string;

  metrics: CustomGraphMetric[];
  per_item?: boolean;
  interval?: GraphInterval;
  legend?: boolean;
}

export class DashboardCustomGraphPreview {
  dashboard_custom_graph_id: string;
  dashboard_group_id: string;
  label: string;

  // item group related
  class?: string;
  hw_type?: string;
  subsystem: string;

  interval?: GraphInterval;
  // only for preview
  path?: string;
  dashboard_group_label?: string;
}

export class CreateDashboardCustomGraphDTO extends ItemGroup {
  dashboard_group_id: string;
  label: string;

  // item group related
  class?: string;
  hw_type?: string;
  subsystem: string;

  metrics: CustomGraphMetric[];
  per_item?: boolean;
  interval?: GraphInterval;
  legend?: boolean;
}

export class DashboardCustomGraphResponseDTO extends BasicResponseDTO {
  data: DashboardCustomGraph;
}

export class DashboardCustomGraphsResponseDTO extends BasicResponseDTO {
  data: DashboardCustomGraph[];
}

export class DashboardCustomGraphPreviewDTO extends BasicResponseDTO {
  data: DashboardCustomGraphPreview[];
}

//#endregion

//#region Grafana

export class GrafanaTreeItem {
  type: 'folder' | 'dashboard';
  uid: number;
  label: string;
  children?: GrafanaTreeItem[];
}

export class FindGrafanaItem {
  dashboard: {
    grafana_dashboard_uid: string;
    label: string;
  };
  grafana_item_uid: string;
  interval?: GraphInterval;
  id?: string;
}

export class CreateGrafanaGraphDTO {
  grafana_dashboard_uid: string;
  url: string;
  tab: string;
  subtab?: string;
  tabId?: string;
  id?: string;
  interval: GraphInterval;
}

export class CreateGrafanaTableDTO {
  grafana_dashboard_uid: string;
  url: string;
  tab: string;
  subtab?: string;
  tabId: string;
  id?: string;
}

export class GrafanaTreeDTO extends BasicResponseDTO {
  data: GrafanaTreeItem[];
}

export class FindGrafanaItemsDTO extends BasicResponseDTO {
  data: FindGrafanaItem[];
}

//#endregion
